import { useEffect, useState } from "react";
import { api, formatDateTime } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip } from "@/components/Chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Shield, ScrollText, Search, Download, Save, Slack, Plus, Trash2, Send, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { downloadCsv } from "@/lib/exports";

// All available permission actions grouped by module
const PERM_MATRIX = [
  { module: "Contacts", actions: ["contacts:read", "contacts:create", "contacts:update", "contacts:delete", "contacts:*"] },
  { module: "Leads", actions: ["leads:read", "leads:create", "leads:update", "leads:delete", "leads:*"] },
  { module: "Tickets", actions: ["tickets:read", "tickets:create", "tickets:update", "tickets:delete", "tickets:*"] },
  { module: "Invoices", actions: ["invoices:read", "invoices:create", "invoices:update", "invoices:delete", "invoices:*"] },
  { module: "Users/Staff", actions: ["users:read", "users:create", "users:update", "users:delete", "users:*"] },
  { module: "Tasks", actions: ["tasks:read", "tasks:*"] },
  { module: "Accounting", actions: ["incomes:read", "incomes:*", "expenses:read", "expenses:*"] },
  { module: "Payroll & Taxes", actions: ["payroll-runs:read", "payroll-runs:*", "taxes:read", "taxes:*"] },
  { module: "Automation", actions: ["automations:read", "automations:*"] },
  { module: "Admin", actions: ["permissions:*", "audit:*", "export:*"] },
];

const ROLE_BADGE = { admin: "danger", manager: "aqua", staff: "info", support_agent: "purple" };
const roleBadgeFor = (role, rank, isCustom) => {
  if (ROLE_BADGE[role]) return ROLE_BADGE[role];
  if (isCustom) return rank >= 2 ? "aqua" : "info";
  return "default";
};

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [rolesMeta, setRolesMeta] = useState([]);
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  // Slack integration state
  const [slackHooks, setSlackHooks] = useState([]);
  const [slackForm, setSlackForm] = useState({ name: "", url: "", channel: "sales" });
  const [slackBusy, setSlackBusy] = useState("");

  useEffect(() => {
    if (user && user.role !== "admin" && user.role !== "manager") {
      toast.error("Admin access required");
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  const loadPerms = () => {
    api.get("/permissions").then(r => setPermissions(r.data));
    api.get("/permissions/roles").then(r => setRolesMeta(r.data)).catch(() => {});
  };
  const loadLogs = () => api.get(`/audit-logs?limit=200${q ? `&action=${encodeURIComponent(q)}` : ""}`)
    .then(r => setLogs(r.data));
  const loadSlackHooks = () => api.get("/integrations/slack/webhooks")
    .then(r => setSlackHooks(r.data)).catch(() => {});

  useEffect(() => { loadPerms(); loadLogs(); loadSlackHooks(); }, []);
  useEffect(() => { const t = setTimeout(loadLogs, 300); return () => clearTimeout(t); }, [q]);

  // Slack handlers
  const createSlack = async () => {
    if (!slackForm.name || !slackForm.url) return toast.error("Name and URL required");
    try {
      await api.post("/integrations/slack/webhooks", slackForm);
      toast.success("Webhook added");
      setSlackForm({ name: "", url: "", channel: "sales" });
      loadSlackHooks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const toggleSlack = async (h) => {
    await api.patch(`/integrations/slack/webhooks/${h.id}`, { enabled: !h.enabled });
    loadSlackHooks();
  };
  const testSlack = async (h) => {
    setSlackBusy(h.id);
    try {
      const { data } = await api.post(`/integrations/slack/webhooks/${h.id}/test`);
      if (data.ok) toast.success("Test message posted to Slack");
      else toast.error(`Failed: ${data.error || data.response}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setSlackBusy("");
    }
  };
  const deleteSlack = async (h) => {
    if (!window.confirm(`Delete webhook "${h.name}"?`)) return;
    await api.delete(`/integrations/slack/webhooks/${h.id}`);
    toast.success("Webhook deleted");
    loadSlackHooks();
  };

  const getRolePerms = (role) => {
    const found = permissions.find(p => p.role === role);
    return dirty[role] ?? (found?.permissions || []);
  };

  const togglePerm = (role, perm) => {
    const current = new Set(getRolePerms(role));
    if (current.has(perm)) current.delete(perm);
    else current.add(perm);
    setDirty(d => ({ ...d, [role]: Array.from(current) }));
  };

  const saveRole = async (role) => {
    if (!dirty[role]) return;
    setSaving(true);
    try {
      await api.patch(`/permissions/${role}`, { permissions: dirty[role] });
      toast.success(`Permissions updated for ${role}`);
      const nd = { ...dirty }; delete nd[role]; setDirty(nd);
      loadPerms();
      loadLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const actionChip = (action) => {
    if (action.includes("denied") || action.includes("failed")) return "danger";
    if (action.includes("delete")) return "warning";
    if (action.includes("create") || action.includes("login")) return "success";
    if (action.includes("update") || action.includes("permission")) return "info";
    return "default";
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Admin Console"
        subtitle="Manage roles, permissions, and view the audit trail"
      />

      <Tabs defaultValue="roles">
        <TabsList>
          <TabsTrigger value="roles" data-testid="tab-roles"><Shield className="w-4 h-4 mr-1.5" /> Roles & Permissions</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><ScrollText className="w-4 h-4 mr-1.5" /> Audit Trail</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations"><Slack className="w-4 h-4 mr-1.5" /> Integrations</TabsTrigger>
        </TabsList>

        <TabsContent value="roles" className="mt-4 space-y-4">
          <div className="text-xs bg-blue-50 text-blue-800 border border-blue-200 px-3 py-2 rounded-md">
            Only admins can modify permissions. A wildcard (<code className="text-blue-900">*</code>) grants everything for that resource.
            Principals can only grant roles at or below their own level.
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {(rolesMeta.length ? rolesMeta.map(r => r.role) : ["admin", "manager", "staff", "support_agent"]).map(role => {
              const perms = new Set(getRolePerms(role));
              const isDirty = !!dirty[role];
              const meta = rolesMeta.find(r => r.role === role);
              const label = meta?.label || role.replace("_", " ");
              return (
                <div key={role} className="kpi-card" data-testid={`role-panel-${role}`}>
                  <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <Chip variant={roleBadgeFor(role, meta?.rank, meta?.is_custom)}>{label}</Chip>
                      {meta?.is_custom && <Chip variant="purple">custom</Chip>}
                      <span className="text-xs text-slate-500 num">{perms.size} permission{perms.size === 1 ? "" : "s"}</span>
                    </div>
                    {isDirty && user?.role === "admin" && (
                      <Button size="sm" onClick={() => saveRole(role)} disabled={saving}
                              className="bg-brand-aqua hover:bg-brand-aqua-hover text-white h-7"
                              data-testid={`save-role-${role}`}>
                        <Save className="w-3.5 h-3.5 mr-1" /> Save
                      </Button>
                    )}
                  </div>
                  <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                    {PERM_MATRIX.map(({ module, actions }) => (
                      <div key={module}>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1.5">{module}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {actions.map(a => {
                            const on = perms.has(a);
                            const disabled = user?.role !== "admin";
                            return (
                              <button
                                key={a}
                                onClick={() => !disabled && togglePerm(role, a)}
                                disabled={disabled}
                                data-testid={`perm-${role}-${a.replace(":", "-").replace("*", "all")}`}
                                className={`px-2 py-0.5 rounded-md text-[11px] font-mono border transition-colors ${
                                  on
                                    ? "bg-brand-aqua-light text-brand-aqua-hover border-brand-aqua/30"
                                    : "bg-slate-50 text-slate-400 border-slate-200 hover:border-slate-300"
                                } ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
                              >
                                {a}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-4 space-y-4">
          <div className="flex justify-between items-center gap-3">
            <div className="relative w-96 max-w-full">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Filter by action (e.g. login, delete, permission)..."
                     value={q} onChange={e => setQ(e.target.value)}
                     className="pl-9" data-testid="audit-search" />
            </div>
            <Button variant="outline" onClick={() => downloadCsv("audit-logs")} data-testid="export-audit-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>

          <div className="table-wrap">
            <table className="w-full text-sm">
              <thead className="table-head">
                <tr>
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">Who</th>
                  <th className="text-left px-4 py-3">Action</th>
                  <th className="text-left px-4 py-3">Resource</th>
                  <th className="text-left px-4 py-3">Result</th>
                  <th className="text-left px-4 py-3">IP</th>
                  <th className="text-left px-4 py-3">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id} className="row" data-testid={`audit-row-${l.id}`}>
                    <td className="px-4 py-3 text-xs text-slate-500 num whitespace-nowrap">{formatDateTime(l.at)}</td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-slate-800">{l.user_name || "—"}</div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400">{l.user_role || ""}</div>
                    </td>
                    <td className="px-4 py-3"><Chip variant={actionChip(l.action)} className="font-mono">{l.action}</Chip></td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      <div className="font-medium">{l.resource_type}</div>
                      {l.resource_id && <div className="text-slate-400 truncate max-w-[140px]">{l.resource_id.slice(0, 8)}…</div>}
                    </td>
                    <td className="px-4 py-3">
                      <Chip variant={l.allow ? "success" : "danger"}>{l.allow ? "allowed" : "denied"}</Chip>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 num">{l.ip || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 max-w-[200px] truncate">
                      {l.details && Object.keys(l.details).length > 0 ? JSON.stringify(l.details) : "—"}
                    </td>
                  </tr>
                ))}
                {logs.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-slate-500 text-sm">No audit entries.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ========== INTEGRATIONS ========== */}
        <TabsContent value="integrations" className="mt-4 space-y-6">
          <div className="kpi-card">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-md bg-[#4A154B] text-white flex items-center justify-center">
                <Slack className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 dark:text-slate-100">Slack</h3>
                <p className="text-xs text-slate-500">Post lead, ticket, tax &amp; overdue-invoice alerts to Slack channels via Incoming Webhooks.</p>
              </div>
            </div>

            <div className="text-xs bg-blue-50 text-blue-800 border border-blue-200 px-3 py-2 rounded-md dark:bg-blue-950/40 dark:text-blue-200 dark:border-blue-900 mt-3">
              <div className="font-medium mb-1">How to get a webhook URL:</div>
              <ol className="list-decimal ml-5 space-y-0.5">
                <li>Open your Slack workspace → <a className="underline" href="https://api.slack.com/apps" target="_blank" rel="noreferrer">api.slack.com/apps</a> → <strong>Create New App</strong> (or use an existing one).</li>
                <li>Go to <strong>Incoming Webhooks</strong> → toggle on.</li>
                <li>Click <strong>Add New Webhook to Workspace</strong>, pick a channel (#sales, #support, #finance …).</li>
                <li>Copy the URL that starts with <code className="text-[11px] bg-white px-1 rounded">https://hooks.slack.com/services/…</code> and paste it below.</li>
              </ol>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
              <div>
                <Label>Name</Label>
                <Input placeholder="e.g. #sales" value={slackForm.name}
                       onChange={e => setSlackForm({ ...slackForm, name: e.target.value })}
                       data-testid="slack-name-input" />
              </div>
              <div className="md:col-span-2">
                <Label>Webhook URL</Label>
                <Input placeholder="https://hooks.slack.com/services/…" value={slackForm.url}
                       onChange={e => setSlackForm({ ...slackForm, url: e.target.value })}
                       className="font-mono text-xs"
                       data-testid="slack-url-input" />
              </div>
              <div>
                <Label>Route to</Label>
                <Select value={slackForm.channel} onValueChange={v => setSlackForm({ ...slackForm, channel: v })}>
                  <SelectTrigger data-testid="slack-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">Sales (new leads)</SelectItem>
                    <SelectItem value="support">Support (new tickets)</SelectItem>
                    <SelectItem value="finance">Finance (tax + overdue)</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <Button onClick={createSlack} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="add-slack-webhook-btn">
                <Plus className="w-4 h-4 mr-1" /> Add webhook
              </Button>
            </div>
          </div>

          {slackHooks.length > 0 && (
            <div className="table-wrap">
              <table className="w-full text-sm">
                <thead className="table-head">
                  <tr>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Route</th>
                    <th className="text-left px-4 py-3">URL</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {slackHooks.map(h => (
                    <tr key={h.id} className="row" data-testid={`slack-hook-${h.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-800">{h.name}</td>
                      <td className="px-4 py-3"><Chip variant="aqua">{h.channel}</Chip></td>
                      <td className="px-4 py-3 font-mono text-[11px] text-slate-500">{h.url_masked || h.url}</td>
                      <td className="px-4 py-3">
                        <Chip variant={h.enabled ? "success" : "default"}>
                          {h.enabled ? <><CheckCircle2 className="w-3 h-3 inline mr-0.5" />enabled</> : <><XCircle className="w-3 h-3 inline mr-0.5" />paused</>}
                        </Chip>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button size="sm" variant="outline" onClick={() => testSlack(h)} disabled={slackBusy === h.id}
                                  className="h-7 px-2 text-xs" data-testid={`slack-test-${h.id}`}>
                            <Send className="w-3.5 h-3.5 mr-1" /> {slackBusy === h.id ? "Sending…" : "Test"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => toggleSlack(h)}
                                  className="h-7 px-2 text-xs" data-testid={`slack-toggle-${h.id}`}>
                            {h.enabled ? "Pause" : "Enable"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => deleteSlack(h)}
                                  className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`slack-delete-${h.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
